import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import ICompAnalysis from "../types/ICompAnalysis";
import { StoreSlice } from "../types/StoreSlice";
import NitroService from "../utils/nitro.service";
import { sortByField } from "../utils/Sort";
import { responseError } from "../utils/utils";
import convertCompAnalysisTruckData from "../utils/convertCompAnalysisTruckData";
import { ICompAnalysisTruck } from "../types/ICompAnalysisTruck";
import { ICompAnalysisRail } from "../types/ICompAnalysisRail";
import convertCompAnalysisRailData from "../utils/convertCompAnalysisRailData";
import IRailFreightDetails from "../types/IRailFreightDetails";
import {
  FreightTruck,
  IDestinationExtension,
  IOriginExtension,
  IQuoteExtension,
  IQuoteExtensionOld,
} from "../types/ICompAnalysisQMD";
import IModes from "../types/IModes";

export type LabelType = "city" | "location" | "prices";

const filterRailTypes = (
  records: ICompAnalysis[],
  mode?: string,
  railType?: string,
) => {
  const filteredRecords = records.filter((r) => {
    if (mode === "RAIL") {
      return r.legs.some((leg) => {
        if (railType === "single") {
          return leg.minCars >= 1 && leg.minCars < 85;
        }
        if (railType === "85+") {
          return leg.minCars >= 85 && leg.minCars < 100;
        }
        if (railType === "100+") {
          return leg.minCars >= 100;
        }
        return true;
      });
    }

    return true;
  });

  return filteredRecords;
};

const updateCAState = (
  records: ICompAnalysis[],
  kochDeliveredPrices: {
    [origin: string]: { deliveredPrice: number; netback: number };
  },
) => {
  // sort records from cheapest to most expensive
  const newKochRecords: ICompAnalysis[] = sortByField(
    records.filter((r) => {
      return r.occupantTypes.indexOf("Koch") !== -1;
    }),
    "initialOffer",
    "ASC",
  );
  const newCompetitorRecords: ICompAnalysis[] = sortByField(
    records.filter((r) => {
      return r.occupantTypes.indexOf("Competitor") !== -1;
    }),
    "initialOffer",
    "ASC",
  );

  const competitorDeliveredPrices = newCompetitorRecords.map((r) => {
    return {
      deliveredPrice: Math.round(r.initialOffer),
      netback: r.maxPrice,
    };
  });
  const updatedState: any = {
    kochRecords: newKochRecords,
    competitorRecords: newCompetitorRecords,
    competitorDeliveredPrices,
    kochDeliveredPrices: {},
  };

  updatedState.selectedKochOrigin = newKochRecords[0]?.id;

  if (newKochRecords.length > 0) {
    const kochDeliveredPrice =
      kochDeliveredPrices[updatedState.selectedKochOrigin]?.deliveredPrice;

    const record = newKochRecords.find(
      (r) => r.id === updatedState.selectedKochOrigin,
    );

    // If the user has modified the koch offer, use that. Else recalculate it using guidance + freight.
    const newKochOffer =
      kochDeliveredPrice || Math.round(record?.initialOffer ?? 0);

    updatedState.kochDeliveredPrices = newKochRecords.map((r) => {
      return {
        deliveredPrice: newKochOffer,
        netback: Math.round(newKochOffer - r.freight),
      };
    });
  }

  return updatedState;
};

export type TariffRowStatus =
  | "dash"
  | "checking"
  | "found"
  | "not found"
  | "try again"
  | "check";

export type PendingTariffPollRequest = {
  rowKey: string;
  quote: IQuoteExtensionOld | IQuoteExtension;
  destination: IDestinationExtension;
  // Experimental: lets a poll run without surfacing checking/countdown UI. Useful for
  // Missing Rail followup items dedup-hidden from the table; remove if it proves unhelpful.
  silent?: boolean;
};

export type MoveToOriginTableFn = (params: {
  quote: IQuoteExtensionOld | IQuoteExtension;
  foundQuote: IQuoteExtensionOld | IQuoteExtension;
}) => void;

export interface ICompetitiveAnalysisSlice {
  isLoading: boolean;
  hadError: boolean;

  load: (params: {
    marketer: string;
    destination: string;
    shipDate: string;
    product: string;
    currency: string;
    uom: string;
    mode?: string;
    railType?: string;
  }) => Promise<void>;
  changeRailType: (mode: string, railType?: string) => void;
  reset: () => void;

  records: ICompAnalysis[];

  kochRecords: ICompAnalysis[];

  kochOffer: number | null;
  setKochOffer: (value: number) => void;

  kochDeliveredPrices: {
    [origin: string]: { deliveredPrice: number; netback: number };
  };
  setKochDeliveredPrice: (
    origin: string,
    value: { deliveredPrice: number; netback: number },
  ) => void;

  competitorRecords: ICompAnalysis[];

  competitorOffer: number;
  setCompetitorOffer: (value: number) => void;

  competitorDeliveredPrices: {
    [origin: string]: { deliveredPrice: number; netback: number };
  };
  setCompetitorDeliveredPrice: (
    origin: string,
    value: { deliveredPrice: number; netback: number },
  ) => void;

  routeValidationOpen: boolean;
  troubleshootQuoteOpen: boolean;
  setTroubleshootQuoteOpen: (open: boolean) => void;
  // Set when Occupants/Logistics actually persist a change during a troubleshoot session.
  insightsPanelSaved: boolean;
  setInsightsPanelSaved: (saved: boolean) => void;
  displayAddProductToDestinationPanel: boolean;
  setDisplayAddProductToDestinationPanel: (open: boolean) => void;
  displayAddProductToOriginPanel: boolean;
  setDisplayAddProductToOriginPanel: (open: boolean) => void;
  originToTroubleshoot: { origin: IOriginExtension; isKoch?: boolean };
  setOriginToTroubleshoot: (origin: IOriginExtension, isKoch?: boolean) => void;
  destinationToTroubleshoot: IDestinationExtension;
  setDestinationToTroubleshoot: (destination: IDestinationExtension) => void;
  expandedDestination?: {
    id: string;
    name: string;
    expandTruckWithNoFreight: boolean;
    expandRailWithNoFreight: boolean;
    expandSurroundingLocations: boolean;
  };
  setExpandedDestination: (
    destination?: {
      id: string;
      name: string;
    },
    expandSettings?: {
      expandTruckWithNoFreight?: boolean;
      expandRailWithNoFreight?: boolean;
      expandSurroundingLocations?: boolean;
    },
  ) => void;
  closeRailFreightDetailsPanel: boolean;
  setRouteValidationOpen: (open: boolean) => void;
  resetRouteValidationState: number;
  triggerReset: () => void;
  originFacilityName: string;
  setOriginFacilityName: (name: string) => void;
  selectYourOfferFreight: boolean;
  setSelectYourOfferFreight: (selected: boolean) => void;
  displayRailFreightDetailsPanel: boolean;
  setDisplayRailFreightDetailsPanel: (open: boolean) => void;
  displayTruckFreightDetailsPanel: boolean;
  setDisplayTruckFreightDetailsPanel: (open: boolean) => void;
  displayAdditionalCarriersPanel: boolean;
  setDisplayAdditionalCarriersPanel: (open: boolean) => void;
  displayInboundModeTogglePanel: boolean;
  setDisplayInboundModeTogglePanel: (open: boolean) => void;
  displayAddInboundRailPanel: boolean;
  setDisplayAddInboundRailPanel: (open: boolean) => void;
  displayTurnOffModePanel: boolean;
  setDisplayTurnOffModePanel: (open: boolean) => void;
  displayRailRateRequestPanel: boolean;
  setDisplayRailRateRequestPanel: (open: boolean) => void;
  exitRequestMultipleModeTrigger: number;
  triggerExitRequestMultipleMode: () => void;
  showRailRateRequestFailedPopup: boolean;
  setShowRailRateRequestFailedPopup: (open: boolean) => void;
  railRateRequestContext: {
    rows: {
      rowKey: string;
      quote: IQuoteExtensionOld | IQuoteExtension;
      silent?: boolean;
    }[];
    destination: IDestinationExtension;
  } | null;
  setRailRateRequestContext: (
    context: {
      rows: {
        rowKey: string;
        quote: IQuoteExtensionOld | IQuoteExtension;
        silent?: boolean;
      }[];
      destination: IDestinationExtension;
    } | null,
  ) => void;
  refetchMultiLocation: number;
  triggerRefetchMultiLocation: () => void;
  requestedRailRateRows: Set<string>;
  addRequestedRailRateRow: (rowKeys: string[]) => void;
  tariffStateByRowKey: Record<
    string,
    { status: TariffRowStatus; rate?: string; secondsRemaining?: number }
  >;
  setTariffStateForRow: (
    rowKey: string,
    entry: {
      status: TariffRowStatus;
      rate?: string;
      secondsRemaining?: number;
    },
  ) => void;
  pendingTariffPollRequests: PendingTariffPollRequest[];
  enqueueTariffPollRequests: (requests: PendingTariffPollRequest[]) => void;
  clearPendingTariffPollRequests: () => void;
  moveToOriginTableRegistry: Record<string, MoveToOriginTableFn>;
  registerMoveToOriginTable: (
    destinationId: string,
    fn: MoveToOriginTableFn,
  ) => void;
  unregisterMoveToOriginTable: (destinationId: string) => void;
  railTeamNotifiedRows: Set<string>;
  addRailTeamNotifiedRow: (rowKey: string) => void;
  surroundingQuoteOpen: boolean;
  setSurroundingQuoteOpen: (open: boolean) => void;
  destinationQuoteOpen: boolean;
  setDestinationQuoteOpen: (open: boolean) => void;
  closeSurroundingQuote: number;
  triggerCloseSurroundingQuote: () => void;
  closeDestinationQuote: number;
  triggerCloseDestinationQuote: () => void;
  expandedDestId?: string;
  setExpandedDestId: (id?: string) => void;
  expandRailOriginsWithNoFreightTrigger: number;
  setExpandRailOriginsWithNoFreightTrigger: () => void;
  resetExpandRailOriginsWithNoFreight: () => void;
  expandTruckOriginsWithNoFreightTrigger: number;
  setExpandTruckOriginsWithNoFreightTrigger: () => void;
  resetExpandTruckOriginsWithNoFreight: () => void;
  modeToTurnOff?: string;
  setModeToTurnOff: (mode?: string) => void;
  additionalCarriers: string[];
  setAdditionalCarriers: (carriers: string[]) => void;
  inboundModes?: IModes;
  setInboundModes: (modes?: IModes) => void;
  anchorForTruckDetailsPanel: HTMLElement | null;
  setAnchorForTruckDetailsPanel: (anchor: HTMLElement | null) => void;
  anchorForAdditionalCarriersPanel: HTMLElement | null;
  setAnchorForAdditionalCarriersPanel: (anchor: HTMLElement | null) => void;
  anchorForInboundModesTogglePanel: HTMLElement | null;
  setAnchorForInboundModesTogglePanel: (anchor: HTMLElement | null) => void;
  setCloseRailFreightDetailsPanel: (close: boolean) => void;
  selectedRailDetailRecord?: IRailFreightDetails;
  selectedTruckDetailRecord?: FreightTruck;
  setSelectedRailDetailRecord: (record: IRailFreightDetails) => void;
  setSelectedTruckDetailRecord: (record: FreightTruck) => void;
  railFreightDetailContext: {
    quote: IQuoteExtensionOld;
    destination: IDestinationExtension;
    rowKey: string;
  } | null;
  setRailFreightDetailContext: (
    context: {
      quote: IQuoteExtensionOld;
      destination: IDestinationExtension;
      rowKey: string;
    } | null,
  ) => void;
  reloadAfterTroubleshoot: number;
  triggerReload: () => void;
  hiddenOrigins: {
    truck: Set<string>;
    rail: Set<string>;
  };
  toggleOriginVisibility: (
    originName: string,
    mode: "truck" | "rail",
    visible: boolean,
  ) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createCompetitiveAnalysisSlice: StoreSlice<ICompetitiveAnalysisSlice> = (
  set,
  get,
) => {
  const initialState = {
    records: [],
    kochRecords: [],
    kochDeliveredPrices: {},
    kochOffer: null,
    competitorRecords: [],
    competitorDeliveredPrices: {},
    competitorOffer: 0,
    isLoading: false,
    hadError: false,
    routeValidationOpen: false,
    troubleshootQuoteOpen: false,
    insightsPanelSaved: false,
    displayAddProductToDestinationPanel: false,
    displayAddProductToOriginPanel: false,
    displayRailFreightDetailsPanel: false,
    displayTruckFreightDetailsPanel: false,
    displayAdditionalCarriersPanel: false,
    displayInboundModeTogglePanel: false,
    displayAddInboundRailPanel: false,
    displayTurnOffModePanel: false,
    displayRailRateRequestPanel: false,
    exitRequestMultipleModeTrigger: 0,
    showRailRateRequestFailedPopup: false,
    railRateRequestContext: null,
    refetchMultiLocation: 0,
    requestedRailRateRows: new Set<string>(),
    railTeamNotifiedRows: new Set<string>(),
    tariffStateByRowKey: {},
    pendingTariffPollRequests: [],
    moveToOriginTableRegistry: {},
    railFreightDetailContext: null,
    surroundingQuoteOpen: false,
    destinationQuoteOpen: false,
    expandRailOriginsWithNoFreightTrigger: 0,
    expandTruckOriginsWithNoFreightTrigger: 0,
    anchorForTruckDetailsPanel: null,
    anchorForAdditionalCarriersPanel: null,
    anchorForInboundModesTogglePanel: null,
    closeRailFreightDetailsPanel: false,
    selectYourOfferFreight: false,
    selectedRailDetailRecord: undefined,
    resetRouteValidationState: 0,
    reloadAfterTroubleshoot: 0,
    closeSurroundingQuote: 0,
    closeDestinationQuote: 0,
    originFacilityName: "",
    hiddenOrigins: {
      truck: new Set<string>(),
      rail: new Set<string>(),
    },
    originToTroubleshoot: {
      origin: {
        id: "",
        name: "",
        coordinate: [0, 0] as [number, number],
        isCity: false,
      } as IOriginExtension,
      isKoch: undefined,
    },
    destinationToTroubleshoot: {
      id: "",
      name: "",
      city: null,
      stateOrProvince: null,
      coordinate: [0, 0],
      isCity: false,
    } as IDestinationExtension,
    additionalCarriers: [],
  };

  return {
    ...initialState,

    load: async (params: {
      marketer?: string;
      destination: string;
      shipDate: string;
      product: string;
      currency: string;
      uom: string;
      mode?: string;
      railType?: string;
    }) => {
      try {
        const { kochDeliveredPrices } = get();
        set({
          isLoading: true,
          hadError: false,
        });

        let records: any[] = [];
        if (params.mode === "TRUCK") {
          set({ records: [] });
          const truckRecords: ICompAnalysisTruck[] =
            (await NitroService.getCompAnalysisTruck(params).catch((err) => {
              responseError(err);
              return [];
            })) || [];
          records = convertCompAnalysisTruckData(truckRecords);
        } else if (params.mode === "RAIL") {
          set({ records: [] });
          const railRecords: ICompAnalysisRail[] =
            (await NitroService.getCompAnalysisRail(params).catch((err) => {
              responseError(err);
              return [];
            })) || [];
          records = convertCompAnalysisRailData(railRecords);
        }

        records.forEach((r) => {
          const isKoch = r.occupantTypes.indexOf("Koch") !== -1;
          // eslint-disable-next-line no-param-reassign
          r.id = `${r.origin.name}-${r.destination.name}-${r.legs[0].mode}${
            r.legs[0].minCars
          }-${isKoch ? "Koch" : "Competitor"}`;
        });

        // Filter description
        // Filter to show 1 Koch location and the new competitor location
        const groupedRecords = records.reduce(
          (acc: { [key: string]: any }, tempRecord) => {
            const key = tempRecord.id;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(tempRecord);
            return acc;
          },
          {},
        );

        const uniqueRecords = Object.values(groupedRecords).map(
          (rows: ICompAnalysis[]) => {
            const latestIndication = sortByField(
              rows,
              "dateLogged",
              "DESC",
            )[0] as ICompAnalysis;

            if (rows.length > 1) {
              latestIndication.occPartyName = "Multiple";
            }

            return latestIndication;
          },
        );
        // End Filter Description

        const filteredRecords = filterRailTypes(
          uniqueRecords,
          params.mode,
          params.railType,
        );
        const updatedState = updateCAState(
          filteredRecords,
          kochDeliveredPrices,
        );

        set({ ...updatedState, records, isLoading: false });
      } catch (err) {
        console.log(err);
        set({
          hadError: true,
          isLoading: false,
        });
      }
    },

    changeRailType: (mode: string, railType?: string) => {
      const { records, kochDeliveredPrices } = get();
      const filteredRecords = filterRailTypes(records, mode, railType);
      const updatedState = updateCAState(filteredRecords, kochDeliveredPrices);

      set(updatedState);
    },

    reset: () => {
      set({
        kochRecords: [],
        kochDeliveredPrices: {},
        kochOffer: null,
        competitorRecords: [],
        competitorDeliveredPrices: {},
        competitorOffer: 0,
        isLoading: false,
        hadError: false,
        resetRouteValidationState: 0,
        reloadAfterTroubleshoot: 0,
      });
    },

    setKochOffer: (value: number) => {
      set({ kochOffer: value, kochDeliveredPrices: {} });
    },

    setKochDeliveredPrice: (
      origin: string,
      value: { deliveredPrice: number; netback: number },
    ) => {
      set((state: ICompetitiveAnalysisSlice) => {
        return {
          kochDeliveredPrices: {
            ...state.kochDeliveredPrices,
            [origin]: value,
          },
        };
      });
    },

    setCompetitorOffer: (value: number) => {
      set({ competitorOffer: value, competitorDeliveredPrices: {} });
    },

    setCompetitorDeliveredPrice: (
      origin: string,
      value: { deliveredPrice: number; netback: number },
    ) => {
      set((state: ICompetitiveAnalysisSlice) => {
        return {
          competitorDeliveredPrices: {
            ...state.competitorDeliveredPrices,
            [origin]: value,
          },
        };
      });
    },

    triggerReset: () =>
      set((state) => ({
        resetRouteValidationState: state.resetRouteValidationState + 1,
      })),

    setRouteValidationOpen: (open: boolean) => {
      set({ routeValidationOpen: open });
    },
    setTroubleshootQuoteOpen: (open: boolean) => {
      set({ troubleshootQuoteOpen: open });
    },
    setInsightsPanelSaved: (saved: boolean) => {
      set({ insightsPanelSaved: saved });
    },
    setDisplayAddProductToDestinationPanel: (open: boolean) => {
      set({ displayAddProductToDestinationPanel: open });
    },
    setDisplayAddProductToOriginPanel: (open: boolean) => {
      set({ displayAddProductToOriginPanel: open });
    },
    setOriginFacilityName: (name: string) => {
      set({ originFacilityName: name });
    },
    setDisplayRailFreightDetailsPanel: (open: boolean) => {
      set({ displayRailFreightDetailsPanel: open });
    },
    setDisplayTruckFreightDetailsPanel: (open: boolean) => {
      set({ displayTruckFreightDetailsPanel: open });
    },
    setDisplayAdditionalCarriersPanel: (open: boolean) => {
      set({ displayAdditionalCarriersPanel: open });
    },
    setDisplayInboundModeTogglePanel: (open: boolean) => {
      set({ displayInboundModeTogglePanel: open });
    },
    setDisplayAddInboundRailPanel: (open: boolean) => {
      set({ displayAddInboundRailPanel: open });
    },
    setDisplayTurnOffModePanel: (open: boolean) => {
      set({ displayTurnOffModePanel: open });
    },
    setDisplayRailRateRequestPanel: (open: boolean) => {
      set({ displayRailRateRequestPanel: open });
    },
    triggerExitRequestMultipleMode: () => {
      set((state) => ({
        exitRequestMultipleModeTrigger:
          state.exitRequestMultipleModeTrigger + 1,
      }));
    },
    setShowRailRateRequestFailedPopup: (open: boolean) => {
      set({ showRailRateRequestFailedPopup: open });
    },
    setRailRateRequestContext: (
      context: {
        rows: { rowKey: string; quote: IQuoteExtensionOld }[];
        destination: IDestinationExtension;
      } | null,
    ) => {
      set({ railRateRequestContext: context });
    },
    triggerRefetchMultiLocation: () => {
      set((state) => ({
        refetchMultiLocation: state.refetchMultiLocation + 1,
        requestedRailRateRows: new Set<string>(),
        tariffStateByRowKey: Object.fromEntries(
          Object.entries(state.tariffStateByRowKey).filter(
            ([, v]) => v.status === "checking",
          ),
        ),
      }));
    },
    addRequestedRailRateRow: (rowKeys: string[]) => {
      set((state) => ({
        requestedRailRateRows: new Set([
          ...state.requestedRailRateRows,
          ...rowKeys,
        ]),
      }));
    },
    setTariffStateForRow: (rowKey, entry) => {
      set((state) => ({
        tariffStateByRowKey: { ...state.tariffStateByRowKey, [rowKey]: entry },
      }));
    },
    enqueueTariffPollRequests: (requests) => {
      set((state) => ({
        pendingTariffPollRequests: [
          ...state.pendingTariffPollRequests,
          ...requests,
        ],
      }));
    },
    clearPendingTariffPollRequests: () => {
      set({ pendingTariffPollRequests: [] });
    },
    registerMoveToOriginTable: (destinationId, fn) => {
      set((state) => ({
        moveToOriginTableRegistry: {
          ...state.moveToOriginTableRegistry,
          [destinationId]: fn,
        },
      }));
    },
    unregisterMoveToOriginTable: (destinationId) => {
      set((state) => {
        const next = { ...state.moveToOriginTableRegistry };
        delete next[destinationId];
        return { moveToOriginTableRegistry: next };
      });
    },
    addRailTeamNotifiedRow: (rowKey: string) => {
      if (!rowKey) {
        return;
      }

      set((state) => ({
        railTeamNotifiedRows: new Set([...state.railTeamNotifiedRows, rowKey]),
      }));
    },
    setSelectedRailDetailRecord: (record: IRailFreightDetails) => {
      set({ selectedRailDetailRecord: record });
    },
    setRailFreightDetailContext: (
      context: {
        quote: IQuoteExtensionOld;
        destination: IDestinationExtension;
        rowKey: string;
      } | null,
    ) => {
      set({ railFreightDetailContext: context });
    },
    setSelectedTruckDetailRecord: (record: FreightTruck) => {
      set({ selectedTruckDetailRecord: record });
    },
    setAnchorForTruckDetailsPanel: (anchor: HTMLElement | null) => {
      set({ anchorForTruckDetailsPanel: anchor });
    },
    setAnchorForAdditionalCarriersPanel: (anchor: HTMLElement | null) => {
      set({ anchorForAdditionalCarriersPanel: anchor });
    },
    setAnchorForInboundModesTogglePanel: (anchor: HTMLElement | null) => {
      set({ anchorForInboundModesTogglePanel: anchor });
    },
    setSelectYourOfferFreight: (selected: boolean) => {
      set({ selectYourOfferFreight: selected });
    },
    setCloseRailFreightDetailsPanel: (close: boolean) => {
      set({ closeRailFreightDetailsPanel: close });
    },
    triggerReload: () =>
      set((state) => ({
        reloadAfterTroubleshoot: state.reloadAfterTroubleshoot + 1,
      })),

    toggleOriginVisibility: (
      originName: string,
      mode: "truck" | "rail",
      visible: boolean,
    ) => {
      set((state) => {
        const newHiddenOrigins = {
          ...state.hiddenOrigins,
          [mode]: new Set(state.hiddenOrigins[mode]),
        };

        if (visible) {
          newHiddenOrigins[mode].delete(originName);
        } else {
          newHiddenOrigins[mode].add(originName);
        }

        return { hiddenOrigins: newHiddenOrigins };
      });
    },
    setOriginToTroubleshoot: (origin: IOriginExtension, isKoch?: boolean) => {
      set({ originToTroubleshoot: { origin, isKoch } });
    },
    setDestinationToTroubleshoot: (destination: IDestinationExtension) => {
      set({ destinationToTroubleshoot: destination });
    },
    setExpandedDestination: (
      destination?: { id: string; name: string },
      expandSettings?: {
        expandTruckWithNoFreight?: boolean;
        expandRailWithNoFreight?: boolean;
        expandSurroundingLocations?: boolean;
      },
    ) => {
      console.log(
        "Setting expanded destination to",
        destination,
        expandSettings,
      );
      // set settings to false by default if not provided
      const settings = {
        expandTruckWithNoFreight:
          expandSettings?.expandTruckWithNoFreight ?? false,
        expandRailWithNoFreight:
          expandSettings?.expandRailWithNoFreight ?? false,
        expandSurroundingLocations:
          expandSettings?.expandSurroundingLocations ?? false,
      };
      const expandedDestination = destination
        ? { id: destination.id, name: destination.name, ...settings }
        : undefined;

      set({
        expandedDestination,
      });
    },
    setAdditionalCarriers: (carriers: string[]) => {
      set({ additionalCarriers: carriers });
    },
    setInboundModes: (modes?: IModes) => {
      set({ inboundModes: modes });
    },
    setModeToTurnOff: (mode?: string) => {
      set({ modeToTurnOff: mode });
    },
    setExpandRailOriginsWithNoFreightTrigger: () =>
      set((state) => ({
        expandRailOriginsWithNoFreightTrigger:
          state.expandRailOriginsWithNoFreightTrigger + 1,
      })),
    resetExpandRailOriginsWithNoFreight: () =>
      set({ expandRailOriginsWithNoFreightTrigger: 0 }),
    setExpandTruckOriginsWithNoFreightTrigger: () =>
      set((state) => ({
        expandTruckOriginsWithNoFreightTrigger:
          state.expandTruckOriginsWithNoFreightTrigger + 1,
      })),
    resetExpandTruckOriginsWithNoFreight: () =>
      set({ expandTruckOriginsWithNoFreightTrigger: 0 }),
    setSurroundingQuoteOpen: (open: boolean) => {
      set({ surroundingQuoteOpen: open });
    },
    setDestinationQuoteOpen: (open: boolean) => {
      set({ destinationQuoteOpen: open });
    },
    triggerCloseSurroundingQuote: () =>
      set((state) => ({
        closeSurroundingQuote: state.closeSurroundingQuote + 1,
      })),
    triggerCloseDestinationQuote: () =>
      set((state) => ({
        closeDestinationQuote: state.closeDestinationQuote + 1,
      })),
    setExpandedDestId: (id?: string) => {
      set({ expandedDestId: id });
    },
  };
};

export const useNitroCAStore = create(
  subscribeWithSelector<ICompetitiveAnalysisSlice>((set: any, get: any) => ({
    ...createCompetitiveAnalysisSlice(set, get),
  })),
);
